/**
 * Custom mega menu (CMM) behaviour: mobile accordion groups and the ZIP lookup
 * shared by the hardiness-zone and Saturday-delivery tools.
 */

const c_zoneStorageKey = 'grf_hardiness_zone';

let c_zoneDataPromise;

function c_getZoneData(url) {
  if (!c_zoneDataPromise) {
    c_zoneDataPromise = fetch(url).then((response) => {
      if (!response.ok) throw new Error('Zone data could not be loaded.');
      return response.json();
    });
  }

  return c_zoneDataPromise;
}

/* Mobile accordion groups -------------------------------------------------- */

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-cmm-toggle]');
  if (!toggle) return;

  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!panel) return;

  const isOpen = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!isOpen));
  panel.hidden = isOpen;
});

/* ZIP lookup --------------------------------------------------------------- */

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-cmm-zip-form]');
  if (!form) return;

  event.preventDefault();

  const input = form.elements.zipcode;
  const status = form.parentElement.querySelector('[data-cmm-zip-status]');
  const zip = input.value.trim();

  const setStatus = (message) => {
    if (status) status.textContent = message;
  };

  if (!/^\d{5}$/.test(zip)) {
    setStatus('Enter a valid five-digit ZIP code.');
    input.focus();
    return;
  }

  const resultUrl = form.dataset.zoneBase;

  if (form.dataset.cmmMode !== 'zone') {
    if (resultUrl) window.location.assign(resultUrl);
    return;
  }

  setStatus('Finding your zone…');

  try {
    const zoneData = await c_getZoneData(form.dataset.zoneMapUrl);
    const zone = zoneData[zip];

    if (!zone) throw new Error('ZIP code not found.');

    try {
      localStorage.setItem(c_zoneStorageKey, zone);
    } catch (storageError) {
      /* Private browsing and blocked storage must not stop the redirect. */
    }

    setStatus(`You’re in zone ${zone}. Opening compatible roses…`);

    if (resultUrl) window.location.assign(resultUrl + encodeURIComponent(zone));
  } catch (error) {
    setStatus('We could not find that ZIP code. Please try again.');
  }
});

/* Desktop panel behaviour ---------------------------------------------------

   The theme opens and closes a mega menu from mouseover/mouseout on the
   <details>. Both events bubble, so every move between child elements looks
   like a leave followed immediately by an enter, and the theme leans on a
   250ms timer to tell a real exit from that noise. That is fragile once a
   panel is as large as these are, and it left menus open after the pointer
   had gone.

   pointerleave does not bubble: it fires once, when the pointer leaves the
   header and everything inside it, the open panel included. One listener on
   the header is therefore enough to close reliably.
*/

const c_headerSelector = '.header';
const c_openPanelSelector = 'details.header__menu-disclosure[open]';

/* A closing panel must stop taking pointer events straight away.

   The theme animates the panel out rather than hiding it, and it re-opens on
   any mouseover. While that leave animation plays, the moving panel can pass
   back under a cursor that has already left, which fires mouseover, calls
   open again mid-flight and snaps the panel back to full opacity before it
   fades a second time. That double fade is the flicker.

   The backdrop is dropped here too rather than waiting on the open attribute,
   which the theme only removes once the animation has finished, leaving the
   page dimmed for a beat after the menu has gone. */
function c_closePanel(details) {
  const panel = details.querySelector('.c-mm');
  if (panel) panel.style.pointerEvents = 'none';

  const disclosure = details.closest('mega-menu-disclosure, dropdown-menu-disclosure');

  if (disclosure && typeof disclosure.toggle === 'function') {
    disclosure.toggle(false);
  } else {
    details.open = false;
  }

  document.body.classList.remove('c-mm-panel-open');
}

function c_openPanel(details) {
  const panel = details.querySelector('.c-mm');
  if (panel) panel.style.pointerEvents = '';
}

function c_closeOpenPanels() {
  document.querySelectorAll(c_openPanelSelector).forEach(c_closePanel);
}

/* Hover intent.

   The theme opens a panel the instant the pointer touches a nav item, with no
   delay. The nav row sits directly above a full width panel, so leaving the
   panel upwards drags the pointer across whichever nav item happens to be
   above that column. That item's panel flashes open and shut on the way past,
   which reads as a flicker.

   Rather than fight the theme's own listener, an open that nothing settled on
   is undone: once a panel opens, it is given a short grace period, and if the
   pointer is by then neither on the nav item nor on the panel, the open was a
   fly-over and is reversed. A deliberate hover is unaffected. */

const c_openIntentMs = 140;
let c_pointerX = -1;
let c_pointerY = -1;

document.addEventListener(
  'pointermove',
  (event) => {
    c_pointerX = event.clientX;
    c_pointerY = event.clientY;
  },
  { passive: true }
);

function c_pointerIsOver(element) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;

  return (
    c_pointerX >= rect.left &&
    c_pointerX <= rect.right &&
    c_pointerY >= rect.top &&
    c_pointerY <= rect.bottom
  );
}

/* Only one mega menu should ever be on screen. The theme opens the new panel
   the moment the pointer reaches the next nav item but closes the previous one
   off its own 250ms leave timer, so the two overlap for a beat and the swap
   reads as a flicker. Closing the outgoing panel as the new one opens makes
   the handover immediate. */
function c_closeOtherPanels(keep) {
  document.querySelectorAll(c_openPanelSelector).forEach((details) => {
    if (details !== keep) c_closePanel(details);
  });
}

function c_dropFlyOverOpen(details) {
  setTimeout(() => {
    if (!details.hasAttribute('open')) return;

    const navItem = details.closest('.header__primary-nav-item');
    const panel = details.querySelector('.c-mm');

    if (c_pointerIsOver(navItem) || c_pointerIsOver(panel)) return;

    c_closePanel(details);
  }, c_openIntentMs);
}

/* The panel is capped to the space below the header so its last row stops
   being cut off on shorter screens. The header's own height moves with the
   announcement bar and the sticky state, so it is measured rather than
   assumed. 64px covers the shell's bottom padding plus breathing room. */
function c_setShellOffset() {
  const header = document.querySelector(c_headerSelector);
  if (!header) return;

  const offset = Math.round(header.getBoundingClientRect().bottom + 64);
  document.documentElement.style.setProperty('--cmm-shell-offset', `${offset}px`);
}

/* The backdrop lives on <body> rather than inside the panel, for the two
   reasons set out above .c-mm-scrim in c-mega-menu.css. It is created here so
   the markup stays free of an element that only exists to be dimmed. */
function c_mountScrim() {
  let scrim = document.querySelector('.c-mm-scrim');

  if (!scrim) {
    scrim = document.createElement('div');
    scrim.className = 'c-mm-scrim';
    scrim.setAttribute('aria-hidden', 'true');
    document.body.appendChild(scrim);
  }

  return scrim;
}

function c_syncScrim() {
  const anyOpen = document.querySelector(c_openPanelSelector) !== null;
  document.body.classList.toggle('c-mm-panel-open', anyOpen);
}

/* Make opening idempotent.

   The theme's hover handler calls toggle(true) on every mouseover, and
   mouseover bubbles, so each crossing between child elements inside the panel
   calls open() again. open() restarts the enter animation, so sweeping the
   pointer out through the panel's contents fires it several times in a row
   and the panel visibly jumps back to full opacity mid-fade. Traced on the
   preview: four open() calls in 90ms on a single exit.

   Re-opening an already open panel is therefore dropped, with one exception:
   if the panel is in the middle of closing, a genuine re-hover must be able
   to catch it, so that case is passed through. */
function c_makeOpenIdempotent(disclosure) {
  if (disclosure.c_patched) return;
  disclosure.c_patched = true;

  const nativeToggle = disclosure.toggle.bind(disclosure);

  disclosure.toggle = function (force) {
    const details = disclosure.querySelector('details.header__menu-disclosure');
    const isOpen = details !== null && details.hasAttribute('open');

    if (force === false) {
      /* A second close restarts the leave animation from the top, which is
         what made the panel jump back to full opacity halfway through fading
         out. One close is enough. */
      if (!isOpen || disclosure.c_closing) return undefined;

      disclosure.c_closing = true;
      return nativeToggle(false);
    }

    if (force === true) {
      if (!isOpen) {
        disclosure.c_closing = false;
        return nativeToggle(true);
      }

      /* Already open. The only reason to open again is to catch a panel that
         is fading out because the pointer came back, so that is checked
         against where the pointer actually is. A mouseover raised by the
         panel animating under a pointer that has already gone does not
         qualify. */
      const pointerReturned =
        disclosure.c_closing &&
        (c_pointerIsOver(details.closest('.header__primary-nav-item')) ||
          c_pointerIsOver(details.querySelector('.c-mm')));

      if (!pointerReturned) return undefined;

      disclosure.c_closing = false;
      return nativeToggle(true);
    }

    return nativeToggle(force);
  };
}

function c_initDesktopPanels() {
  const header = document.querySelector(c_headerSelector);
  if (!header) return;

  header.addEventListener('pointerleave', c_closeOpenPanels);

  c_mountScrim();

  /* The theme owns opening and closing, so the backdrop follows the panels
     rather than trying to predict them. Watching the open attribute catches
     every route in, hover, click and keyboard alike. */
  header.querySelectorAll('mega-menu-disclosure, dropdown-menu-disclosure').forEach(c_makeOpenIdempotent);

  header.querySelectorAll('details.header__menu-disclosure').forEach((details) => {
    new MutationObserver(() => {
      c_syncScrim();

      if (!details.hasAttribute('open')) {
        const disclosure = details.closest('mega-menu-disclosure, dropdown-menu-disclosure');
        if (disclosure) disclosure.c_closing = false;
      }

      if (details.hasAttribute('open')) {
        c_openPanel(details);
        c_closeOtherPanels(details);
        c_dropFlyOverOpen(details);

        /* A closed panel has no width, so a rail inside it cannot tell whether
           it overflows until the panel is on screen. */
        details.querySelectorAll('[data-cmm-rail]').forEach((rail) => {
          c_initRail(rail);
          requestAnimationFrame(() => c_syncRailArrows(rail));
        });
      }
    }).observe(details, { attributes: true, attributeFilter: ['open'] });
  });
  c_syncScrim();

  c_setShellOffset();
  window.addEventListener('resize', c_setShellOffset);
  window.addEventListener('scroll', c_setShellOffset, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', c_initDesktopPanels);
} else {
  c_initDesktopPanels();
}

/* Swatch rail ----------------------------------------------------------------

   The "shop by colour" rows carry eleven collections and rarely fit. The row
   already scrolls with its scrollbar hidden, which leaves no affordance and no
   way to move it with a normal mouse, so arrows are shown when it overflows and
   a vertical wheel is translated into horizontal movement.
*/

const c_railSelector = '[data-cmm-rail]';

function c_syncRailArrows(rail) {
  const track = rail.querySelector('[data-cmm-rail-track]');
  const prev = rail.querySelector('[data-cmm-rail-prev]');
  const next = rail.querySelector('[data-cmm-rail-next]');
  if (!track || !prev || !next) return;

  /* 2px of slack keeps the arrow from flickering on sub-pixel scroll ends. */
  const overflows = track.scrollWidth - track.clientWidth > 2;

  prev.hidden = !overflows || track.scrollLeft <= 2;
  next.hidden = !overflows || track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
}

function c_scrollRail(rail, direction) {
  const track = rail.querySelector('[data-cmm-rail-track]');
  if (!track) return;

  track.scrollBy({ left: Math.round(track.clientWidth * 0.8) * direction, behavior: 'smooth' });
}

function c_initRail(rail) {
  if (rail.c_ready) return;
  rail.c_ready = true;

  const track = rail.querySelector('[data-cmm-rail-track]');
  if (!track) return;

  rail.querySelector('[data-cmm-rail-prev]')?.addEventListener('click', () => c_scrollRail(rail, -1));
  rail.querySelector('[data-cmm-rail-next]')?.addEventListener('click', () => c_scrollRail(rail, 1));

  track.addEventListener('scroll', () => c_syncRailArrows(rail), { passive: true });

  /* A wheel is vertical, the row is horizontal. The event is only taken over
     when there is somewhere left to go in that direction, so reaching either
     end hands scrolling back to the page rather than trapping it. */
  track.addEventListener(
    'wheel',
    (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      if (track.scrollWidth - track.clientWidth <= 2) return;

      const atStart = track.scrollLeft <= 0 && event.deltaY < 0;
      const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth && event.deltaY > 0;
      if (atStart || atEnd) return;

      event.preventDefault();
      track.scrollLeft += event.deltaY;
    },
    { passive: false }
  );

  /* A rail inside a closed desktop panel or an unopened drawer screen has no
     width, so it cannot tell whether it overflows until it is on screen. The
     box changing size is the one signal every route in shares. */
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(() => c_syncRailArrows(rail)).observe(track);
  }

  c_syncRailArrows(rail);
}

function c_initRails() {
  document.querySelectorAll(c_railSelector).forEach((rail) => {
    c_initRail(rail);
    /* Panels are hidden at load, so widths only become real once one opens. */
    requestAnimationFrame(() => c_syncRailArrows(rail));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', c_initRails);
} else {
  c_initRails();
}

window.addEventListener('resize', () => {
  document.querySelectorAll(c_railSelector).forEach(c_syncRailArrows);
});
